import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { PullService } from '@/features/pulls/services/pull-service';

/**
 * Hook for pull-related actions using service layer with TanStack Query
 * Provides loading states, error handling, and success notifications
 */
export function usePullActions(owner: string, repo: string, pullNumber: number) {
  const queryClient = useQueryClient();

  const generateReview = useMutation({
    mutationFn: (params: {
      installationId: number;
      reviewType?: 'full' | 'incremental' | 'range';
      fromCommit?: string;
      toCommit?: string;
      customPrompt?: string;
    }) => {
      console.log('[usePullActions] generateReview mutation called:', {
        owner,
        repo,
        pullNumber,
        ...params,
      });
      
      return PullService.generateReview({
        owner,
        repo,
        pullNumber,
        ...params,
      });
    },
    onSuccess: () => {
      // Invalidate relevant queries to refresh data
      queryClient.invalidateQueries({
        queryKey: ['pulls', owner, repo, pullNumber],
      });
      queryClient.invalidateQueries({
        queryKey: ['reviews', owner, repo, pullNumber],
      });
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to generate review');
    },
  });

  const generateWalkthrough = useMutation({
    mutationFn: () =>
      PullService.generateWalkthrough({
        owner,
        repo,
        pullNumber,
      }),
    onSuccess: () => {
      toast.success('Walkthrough generated successfully!');
      // Invalidate queries to refresh walkthrough data
      queryClient.invalidateQueries({
        queryKey: ['pulls', owner, repo, pullNumber, 'walkthrough'],
      });
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to generate walkthrough');
    },
  });


  return {
    generateReview: {
      mutate: generateReview.mutate,
      isLoading: generateReview.isPending,
      error: generateReview.error,
    },
    generateWalkthrough: {
      mutate: generateWalkthrough.mutate,
      isLoading: generateWalkthrough.isPending,
      error: generateWalkthrough.error,
    },
    // Convenience properties for checking if any action is loading
    isAnyActionLoading: generateReview.isPending || generateWalkthrough.isPending,
  };
}
